import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { useProject } from '../context/ProjectContext';
import { getApiUrl, axiosConfig } from '../config/api';
import './SEOAnalyzer.css';

const MIN_TITLE = 30;
const MAX_TITLE = 60;
const MIN_DESCRIPTION = 120;
const MAX_DESCRIPTION = 160;

// Fun facts reused from Dashboard for loading screens
const funFacts = [
  "Did you know? Google processes over 8.5 billion searches per day!",
  "Did you know? 75% of users never scroll past the first page of search results.",
  "Did you know? Websites that load in 2 seconds have a 9% bounce rate, while those taking 5 seconds have a 38% bounce rate.",
  "Did you know? Mobile-first indexing means Google primarily uses the mobile version of your site for ranking.",
  "Did you know? Pages with images get 94% more views than those without.",
  "Did you know? The average user reads only 20% of the text on a webpage.",
  "Did you know? Websites with SSL certificates (HTTPS) rank higher in Google search results.",
  "Did you know? The first 3 seconds of page load time impact 53% of mobile site visits.",
  "Did you know? Internal linking can improve your site's SEO and help users navigate better.",
  "Did you know? Long-form content (2000+ words) tends to rank higher in search results.",
  "Did you know? Schema markup can help your website appear in rich snippets, increasing click-through rates.",
  "Did you know? Core Web Vitals are now a ranking factor for Google search results.",
  "Did you know? Websites with fast loading times have better conversion rates.",
  "Did you know? Alt text for images not only helps SEO but also improves accessibility.",
  "Did you know? A well-structured sitemap helps search engines crawl and index your site more effectively."
];

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'good':
      return (
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="10" cy="10" r="9" opacity="0.25"></circle>
          <path d="M6 10l2.5 2.5L14 7"></path>
        </svg>
      );
    case 'warning':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      );
    case 'error':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      );
  }
};

const CircularProgress = ({ percentage, size = 80, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  const getColor = () => {
    if (percentage >= 80) return 'var(--success-color)';
    if (percentage >= 50) return 'var(--warning-color)';
    return 'var(--error-color)';
  };

  return (
    <div className="circular-progress-container" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="circular-progress-svg">
        <circle
          className="circular-progress-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="circular-progress-fg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          stroke={getColor()}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="circular-progress-text">
        <span className="circular-progress-percentage">{Math.round(percentage)}</span>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value, remark, status = 'good', additionalContent }) => {
  const resolvedValue =
    value !== undefined && value !== null && value !== ''
      ? value
      : <span className="muted">Not available</span>;

  return (
    <div className={`detail-row detail-${status}`}>
      <div className="row-status-section">
        <div className={`row-status-icon status-${status}`}>
          <StatusIcon status={status} />
        </div>
      </div>
      <div className="row-metric-section">
        <div className="row-label">{label}</div>
      </div>
      <div className="row-remark-section">
        <div className="combined-explanation">
          <span className="explanation-value">{resolvedValue}</span>
          {remark && (
            <span className="explanation-remark"> — {remark}</span>
          )}
        </div>
        {additionalContent && (
          <div className="additional-content">
            {additionalContent}
          </div>
        )}
      </div>
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const formatList = (items = []) => {
  if (!items.length) return <span className="muted">No data</span>;
  return (
    <ul className="inline-list">
      {items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );
};

const formatTags = (items = []) => {
  if (!items.length) return <span className="muted">No data</span>;
  return (
    <div className="tag-container">
      {items.map((item, idx) => (
        <span key={idx} className="schema-tag">
          {item.replace('https://schema.org/', '').replace('http://schema.org/', '')}
        </span>
      ))}
    </div>
  );
};

const formatHeadings = (items = []) => {
  if (!items.length) return <span className="muted">No data</span>;
  return (
    <div className="heading-container">
      {items.map((item, idx) => (
        <div key={idx} className="heading-box">
          {item}
        </div>
      ))}
    </div>
  );
};

const formatImageList = (images, options = {}) => {
  const { showSize = false, remarkText = '' } = options;
  if (!images.length) return <span className="muted">No data</span>;
  
  // Helper function to extract title from URL if not provided
  const getImageTitle = (img, src) => {
    if (typeof img === 'object' && img.title && img.title.trim()) {
      return img.title;
    }
    // Fallback: extract from URL
    try {
      const url = new URL(src);
      const pathname = url.pathname;
      const filename = pathname.split('/').pop();
      if (filename && filename.includes('.')) {
        const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
        if (nameWithoutExt) {
          return decodeURIComponent(nameWithoutExt)
            .replace(/[-_]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        }
      }
    } catch (e) {
      // If URL parsing fails, try simple extraction
      // eslint-disable-next-line no-useless-escape
      const match = src.match(/\/([^\/]+\.(jpg|jpeg|png|gif|webp|svg))$/i);
      if (match && match[1]) {
        return match[1].split('.')[0].replace(/[-_]/g, ' ').trim();
      }
    }
    return null;
  };
  
  return (
    <div className="image-list-container">
      <div className="image-preview-item image-preview-header">
        <div className="image-preview-section image-preview-left">
          <span className="image-header-label">Image</span>
        </div>
        <div className="image-preview-section image-preview-info">
          <span className="image-header-label">Details</span>
        </div>
        <div className="image-preview-section image-preview-remark">
          <span className="image-header-label">Remarks</span>
        </div>
      </div>
      {images.map((img, idx) => {
        const src = typeof img === 'string' ? img : img.src;
        let title = typeof img === 'object' && img.title ? img.title : null;
        if (!title || !title.trim()) {
          title = getImageTitle(img, src);
        }
        const size = typeof img === 'object' && img.size ? img.size : null;
        const resolvedRemark =
          typeof remarkText === 'function'
            ? remarkText(img)
            : remarkText || '';

        return (
          <div key={idx} className="image-preview-item image-preview-row">
            <div className="image-preview-section image-preview-left">
              <img 
                src={src} 
                alt="Preview" 
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                className="image-thumbnail"
              />
              <div className="image-placeholder" style={{ display: 'none' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </div>
            </div>
            <div className="image-preview-section image-preview-info">
              {title && title.trim() && (
                <div className="image-title">{title}</div>
              )}
              <a href={src} target="_blank" rel="noopener noreferrer" className="image-link">
                {src}
              </a>
              {showSize && size && (
                <span className="image-size">{size}</span>
              )}
            </div>
            <div className="image-preview-section image-preview-remark">
              <div className="image-remark-label">Recommended fix</div>
              <p className="image-remark-text">
                {resolvedRemark || 'Review this asset for potential improvements.'}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const formatLinkList = (links) => {
  if (!links.length) return <span className="muted">No data</span>;

  return (
    <div className="link-list-container">
      <div className="link-preview-item link-preview-header">
        <div className="link-preview-section link-preview-type">
          <span className="link-header-label">Link Type</span>
        </div>
        <div className="link-preview-section link-preview-anchor">
          <span className="link-header-label">Anchor Text</span>
        </div>
        <div className="link-preview-section link-preview-url">
          <span className="link-header-label">URL</span>
        </div>
      </div>
      {links.map((link, idx) => {
        const tagLabel = link.tagLabelOverride
          ? link.tagLabelOverride
          : link.isNofollow
            ? 'Nofollow'
            : link.isExternal
              ? 'External'
              : 'Internal';
        const tagClass = link.tagClassOverride
          ? link.tagClassOverride
          : link.isNofollow
            ? 'tag-nofollow'
            : link.isExternal
              ? 'tag-external'
              : 'tag-internal';

        return (
          <div key={idx} className="link-preview-item link-preview-row">
            <div className="link-preview-section link-preview-type">
              <span className={`link-type-tag ${tagClass}`}>
                {tagLabel}
              </span>
            </div>
            <div className="link-preview-section link-preview-anchor">
              {link.image ? (
                <div className="link-image-anchor">
                  <img src={link.image} alt="Link preview" onError={(e) => (e.target.style.display = 'none')} />
                  <span>Image Link</span>
                </div>
              ) : (
                <span className="link-anchor">{link.text && link.text.trim() ? link.text : '-'}</span>
              )}
            </div>
            <div className="link-preview-section link-preview-url">
              <a href={link.url} target="_blank" rel="noopener noreferrer" className="link-url">
                {link.url}
              </a>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getLengthFeedback = (text, min, max, label) => {
  const labelLower = label.toLowerCase();
  if (!text) {
    return {
      remark: `You don't have a ${labelLower} set. This is the text that appears in search results and browser tabs. Add one that describes your page clearly and includes important keywords.`,
      status: 'warning',
    };
  }
  if (text.length < min) {
    return {
      remark: `Your ${labelLower} is too short (${text.length} characters). Search engines prefer ${min}–${max} characters. Make it longer to better describe your page and improve how it appears in search results.`,
      status: 'warning',
    };
  }
  if (text.length > max) {
    return {
      remark: `Your ${labelLower} is too long (${text.length} characters). Search engines typically cut off text after ${max} characters, so keep it between ${min}–${max} characters to ensure the full message is visible.`,
      status: 'warning',
    };
  }
  return {
    remark: `Your ${labelLower} is the perfect length (${text.length} characters). This helps it display fully in search results and gives you the best chance of attracting clicks.`,
    status: 'good',
  };
};

const getRobotsRemark = (robots) => {
  if (!robots.exists) {
    return {
      remark: 'Your website doesn\'t have a robots.txt file. This file tells search engines which pages they can and cannot visit. You should create one to have better control over how your site is crawled.',
      status: 'warning',
    };
  }
  if (!robots.accessible) {
    return {
      remark: 'Your robots.txt file exists but search engines can\'t access it. This might be due to server settings or permissions. Fix this so search engines can read your crawling instructions.',
      status: 'warning',
    };
  }
  return {
    remark: 'Your robots.txt file is working properly and search engines can access it. Make sure the rules inside match what you want search engines to see.',
    status: 'good',
  };
};

const getSchemaRemark = (schema) => {
  if (!schema.found) {
    return {
      remark: 'Your website doesn\'t use structured data (schema markup). This special code helps search engines understand your content better and can make your listings look more attractive in search results with stars, prices, or other rich information.',
      status: 'warning',
    };
  }
  return {
    remark: `Great! Your website uses structured data (${schema.types.length || 1} type${schema.types.length !== 1 ? 's' : ''}). This helps search engines understand your content and can make your search listings more appealing. Make sure to test it with Google's Rich Results Test tool.`,
    status: 'good',
  };
};

const getSitemapRemark = (sitemaps) => {
  if (!sitemaps.found) {
    return {
      remark: 'No sitemap found. A sitemap is like a map of your website that helps search engines discover all your pages. Create a sitemap.xml file and tell search engines about it through Google Search Console.',
      status: 'warning',
    };
  }
  return {
    remark: `Good! You have ${sitemaps.sitemapUrls.length} sitemap${sitemaps.sitemapUrls.length !== 1 ? 's' : ''} that help search engines find your pages. Keep them updated whenever you add or remove pages from your website.`,
    status: 'good',
  };
};

// eslint-disable-next-line no-unused-vars
const getHeadingRemark = (headings) => {
  if (headings.h1Count === 0) {
    return 'Your page is missing the main heading (H1 tag). This is the most important heading that tells both visitors and search engines what your page is about. Add one clear, descriptive H1 at the top of your main content.';
  }
  if (headings.h1Count > 1) {
    return `You have ${headings.h1Count} main headings (H1 tags) on this page. It's best to have only one H1 per page to clearly show what the page is about. Remove the extra ones and use H2 tags for other important sections instead.`;
  }
  if (!headings.h2.length) {
    return 'Your page doesn\'t have any section headings (H2 tags). These help organize your content into clear sections, making it easier for both readers and search engines to understand your page structure. Add H2 tags to break up your content.';
  }
  return 'Your heading structure looks good! You have one main heading (H1) and section headings (H2) to organize your content. Keep this structure consistent throughout your page.';
};

const getKeywordRemark = (keywords) => {
  if (!keywords.topKeywords.length) {
    return 'We couldn\'t find any keywords that appear multiple times on your page. While you don\'t want to overuse keywords, having important terms appear naturally throughout your content helps search engines understand what your page is about.';
  }
  return 'We found the main keywords that appear most often on your page. Make sure these keywords match what people are searching for and that they appear naturally in your content—don\'t stuff them in just to rank higher.';
};

const getContentRemark = (content) => {
  if (content.wordCount < 300) {
    return {
      remark: `Your page has ${content.wordCount} words, which is quite short. Search engines and visitors prefer pages with more substantial content (typically 300+ words). Add more detailed information, examples, or explanations to make your page more valuable.`,
      status: 'warning',
    };
  }
  return {
    remark: `Your page has ${content.wordCount} words, which is a good amount of content. This gives you enough space to provide valuable information to visitors and helps search engines understand your page better. Keep your content well-organized and update it regularly.`,
    status: 'good',
  };
};

const getImageRemark = (images) => {
  if (images.withoutAlt > 0) {
    return {
      remark: `${images.withoutAlt} of your images don't have alt text (alternative text). Alt text describes images to people who can't see them and helps search engines understand what your images show. Add descriptive alt text to all images for better accessibility and SEO.`,
      status: 'warning',
    };
  }
  return {
    remark: 'Excellent! All your images have alt text. This helps visually impaired visitors understand your images and helps search engines index your images properly. Keep your alt text descriptions clear and relevant to each image.',
    status: 'good',
  };
};

const getLinkRemark = (links) => {
  if (links.external > links.internal) {
    return {
      remark: 'You have more links pointing to other websites than links to other pages on your own site. While external links are fine, having more internal links helps visitors navigate your site and helps search engines discover all your pages. Try adding more links to your other relevant pages.',
      status: 'warning',
    };
  }
  return {
    remark: 'You have a good balance of internal links (to your own pages) and external links (to other sites). Internal links help visitors explore your site and help search engines find all your content. Keep this balance while making sure your links are relevant and useful.',
    status: 'good',
  };
};

// Score calculation functions
const calculateSecurityScore = (security) => {
  let score = 0;
  let total = 3;
  
  if (security.sslCertificate) score += 1;
  if (security.httpsRedirection) score += 1;
  if (security.wwwRedirection?.redirected) score += 1;
  
  return (score / total) * 100;
};

const calculateRobotsScore = (robots) => {
  if (!robots.exists) return 0;
  if (!robots.accessible) return 50;
  return 100;
};

const calculateSchemaSitemapScore = (schema, sitemaps) => {
  let score = 0;
  let total = 2;
  
  if (schema.found) score += 1;
  if (sitemaps.found) score += 1;
  
  return (score / total) * 100;
};

const calculateMetaScore = (meta) => {
  let score = 0;
  let total = 4;
  
  // Title length
  if (meta.title && meta.title.length >= MIN_TITLE && meta.title.length <= MAX_TITLE) score += 1;
  // Description length
  if (meta.description && meta.description.length >= MIN_DESCRIPTION && meta.description.length <= MAX_DESCRIPTION) score += 1;
  // Indexing status
  if (meta.indexingStatus === 'index') score += 1;
  // OG Image
  if (meta.ogImage) score += 1;
  
  return (score / total) * 100;
};

const calculateTechnicalScore = (meta) => {
  let score = 0;
  let total = 3;
  
  // Viewport
  if (meta.viewport) score += 1;
  // Charset
  if (meta.charset) score += 1;
  // Language
  if (meta.language) score += 1;
  
  return (score / total) * 100;
};

const calculateHeadingScore = (headings) => {
  let score = 0;
  let total = 2;
  
  if (headings.h1Count === 1) score += 1;
  if (headings.h2.length > 0) score += 1;
  
  return (score / total) * 100;
};

const calculateKeywordScore = (keywords) => {
  if (keywords.topKeywords.length > 0) return 100;
  return 50;
};

const calculateContentScore = (content) => {
  if (content.wordCount >= 300) return 100;
  if (content.wordCount >= 150) return 70;
  return 40;
};

const calculateImageScore = (images) => {
  if (images.total === 0) return 100; // No images is fine
  
  let score = 0;
  let total = 3;
  
  // Alt text coverage (1 point if all images have alt text)
  if (images.withoutAlt === 0) score += 1;
  else if (images.withAlt > 0) score += (images.withAlt / images.total);
  
  // Large images (1 point if no large images)
  if (images.largeImages === 0) score += 1;
  
  // WebP versions (1 point if all have WebP or no images need it)
  if (images.missingWebpList.length === 0) score += 1;
  else if (images.total > 0) {
    const webpCoverage = (images.total - images.missingWebpList.length) / images.total;
    score += webpCoverage;
  }
  
  return (score / total) * 100;
};

const calculateLinkScore = (links) => {
  if (links.total === 0) return 50;
  if (links.internal >= links.external) return 100;
  return 70;
};

const SEOAnalyzer = ({ user, onLogout, currentProject }) => {
  const { updateProject, userPlan } = useProject();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState('light');
  const reportRef = useRef(null);
  const [showPageLoader, setShowPageLoader] = useState(false);
  const [pageLoaderMessage, setPageLoaderMessage] = useState('Loading...');
  const [funFact, setFunFact] = useState('');
  const [analyzeCountdown, setAnalyzeCountdown] = useState(0);
  const [downloadCountdown, setDownloadCountdown] = useState(0);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Helper function to get cache key for a project
  const getCacheKey = (projectUrl) => {
    if (!projectUrl) return null;
    // Version cache keys when audit logic changes so stale results
    // don’t keep showing outdated findings.
    return `siteAudit_v2_${projectUrl}`;
  };

  // Helper function to get countdown key for analyze action
  const getAnalyzeCountdownKey = (projectUrl) => {
    if (!projectUrl) return null;
    return `siteAudit_countdown_analyze_${projectUrl}`;
  };

  // Helper function to get countdown key for download action
  const getDownloadCountdownKey = (projectUrl) => {
    if (!projectUrl) return null;
    return `siteAudit_countdown_download_${projectUrl}`;
  };

  // Check analyze countdown on mount and when project/results/url changes
  useEffect(() => {
    const targetUrl = currentProject?.url || results?.url || url;
    if (!targetUrl) {
      setAnalyzeCountdown(0);
      return;
    }

    const analyzeKey = getAnalyzeCountdownKey(targetUrl);
    if (!analyzeKey) {
      setAnalyzeCountdown(0);
      return;
    }

    const savedCountdownEnd = localStorage.getItem(analyzeKey);
    if (savedCountdownEnd) {
      const endTime = parseInt(savedCountdownEnd, 10);
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
      
      if (remaining > 0) {
        setAnalyzeCountdown(remaining);
      } else {
        localStorage.removeItem(analyzeKey);
        setAnalyzeCountdown(0);
      }
    } else {
      setAnalyzeCountdown(0);
    }
  }, [currentProject?.url, results?.url, url]);

  // Check download countdown on mount and when project/results/url changes
  useEffect(() => {
    const targetUrl = currentProject?.url || results?.url || url;
    if (!targetUrl) {
      setDownloadCountdown(0);
      return;
    }

    const downloadKey = getDownloadCountdownKey(targetUrl);
    if (!downloadKey) {
      setDownloadCountdown(0);
      return;
    }

    const savedCountdownEnd = localStorage.getItem(downloadKey);
    if (savedCountdownEnd) {
      const endTime = parseInt(savedCountdownEnd, 10);
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
      
      if (remaining > 0) {
        setDownloadCountdown(remaining);
      } else {
        localStorage.removeItem(downloadKey);
        setDownloadCountdown(0);
      }
    } else {
      setDownloadCountdown(0);
    }
  }, [currentProject?.url, results?.url, url]);

  // Load saved audit results from localStorage when component mounts or project changes
  useEffect(() => {
    if (!currentProject) {
      setResults(null);
      return;
    }

    const projectUrl = currentProject.url;
    if (!projectUrl) {
      setResults(null);
      return;
    }

    // Try to load from localStorage cache first
    const cacheKey = getCacheKey(projectUrl);
    if (cacheKey) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          const cacheTimestamp = parsed.cachedAt || parsed.timestamp;
          
          if (cacheTimestamp) {
            const savedTime = new Date(cacheTimestamp);
            const now = new Date();
            const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
            
            // Use cached data if less than 24 hours old
            if (hoursDiff < 24 && parsed.data) {
              setResults(parsed.data);
              return;
            } else {
              // Cache expired, remove it
              localStorage.removeItem(cacheKey);
            }
          } else if (parsed.data) {
            // Legacy cache without timestamp, use it but mark for refresh
            setResults(parsed.data);
            return;
          }
        }
      } catch (err) {
        console.error('Error loading cached audit results:', err);
        // Remove corrupted cache
        if (cacheKey) localStorage.removeItem(cacheKey);
      }
    }

    // Fallback to project's auditResults if no cache
    if (currentProject.auditResults) {
      setResults(currentProject.auditResults);
    } else {
      setResults(null);
    }
  }, [currentProject]);

  // Analyze countdown timer effect
  useEffect(() => {
    if (analyzeCountdown <= 0) return;
    
    const targetUrl = currentProject?.url || results?.url || url;
    const analyzeKey = getAnalyzeCountdownKey(targetUrl);
    if (!analyzeKey) return;

    const timer = setInterval(() => {
      setAnalyzeCountdown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem(analyzeKey);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [analyzeCountdown, currentProject?.url, results?.url, url]);

  // Download countdown timer effect
  useEffect(() => {
    if (downloadCountdown <= 0) return;
    
    const targetUrl = currentProject?.url || results?.url || url;
    const downloadKey = getDownloadCountdownKey(targetUrl);
    if (!downloadKey) return;

    const timer = setInterval(() => {
      setDownloadCountdown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem(downloadKey);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [downloadCountdown, currentProject?.url, results?.url, url]);

  // eslint-disable-next-line no-unused-vars
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  // Helper function to extract text from element, hiding unwanted elements
  // eslint-disable-next-line no-unused-vars
  const extractTextContent = (element) => {
    if (!element) return '';
    
    // Clone the element to avoid modifying the original
    const clone = element.cloneNode(true);
    
    // Remove elements that should be hidden in PDF
    const elementsToHide = clone.querySelectorAll(
      '.header, .analyzer-form, .theme-toggle, .download-report-btn, .timestamp, .results-title'
    );
    elementsToHide.forEach(el => el.remove());
    
    // Extract text content
    return clone.innerText || clone.textContent || '';
  };

  // Helper function to split text into lines that fit page width
  const splitTextIntoLines = (pdf, text, maxWidth, fontSize) => {
    if (!text) return [];
    
    const lines = [];
    const words = text.split(' ');
    let currentLine = '';
    
    // Set font size for accurate width calculation
    pdf.setFontSize(fontSize);
    
    words.forEach(word => {
      // Handle very long words that exceed maxWidth
      const wordWidth = pdf.getTextWidth(word);
      if (wordWidth > maxWidth) {
        // If current line has content, save it first
        if (currentLine) {
          lines.push(currentLine);
          currentLine = '';
        }
        // Break long word into chunks
        let remainingWord = word;
        while (remainingWord.length > 0) {
          let chunk = '';
          for (let i = 0; i < remainingWord.length; i++) {
            const testChunk = chunk + remainingWord[i];
            if (pdf.getTextWidth(testChunk) <= maxWidth) {
              chunk = testChunk;
            } else {
              break;
            }
          }
          if (chunk) {
            lines.push(chunk);
            remainingWord = remainingWord.substring(chunk.length);
          } else {
            // Single character is too wide, add it anyway
            lines.push(remainingWord[0]);
            remainingWord = remainingWord.substring(1);
          }
        }
      } else {
        // Normal word processing
        const testLine = currentLine + (currentLine ? ' ' : '') + word;
        const textWidth = pdf.getTextWidth(testLine);
        
        if (textWidth > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines.length > 0 ? lines : [''];
  };

  // Helper function to render robots.txt table without header (content only)
  const renderRobotsTableWithoutHeader = (pdf, rows, startX, startY, colWidths, margin, pageHeight, checkNewPageFn, rowHeight = 7) => {
    let yPos = startY;
    const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);
    
    // Set font for data rows
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    
    // Draw data rows with page break handling
    rows.forEach((row, rowIndex) => {
      const cellText = row[0] || '-';
      
      // Split by newlines to preserve line breaks
      const contentLines = cellText.split('\n');
      
      // Calculate total height needed for all lines
      let totalLines = 0;
      contentLines.forEach(line => {
        const wrappedLines = splitTextIntoLines(pdf, line.trim(), colWidths[0] - 4, 8);
        totalLines += wrappedLines.length || 1; // At least 1 line even if empty
      });
      
      const cellHeight = Math.max(totalLines * 4 + 4, rowHeight);
      
      // Check if we need a new page
      if (yPos + cellHeight > pageHeight - margin) {
        pdf.addPage();
        yPos = margin;
      }
      
      // Draw row with unified border
      pdf.rect(startX, yPos, totalWidth, cellHeight);
      
      // Draw content line by line, preserving newlines
      let currentY = yPos + 5;
      contentLines.forEach((line, lineIndex) => {
        const trimmedLine = line.trim();
        if (trimmedLine || lineIndex === 0) { // Include empty lines or first line
          const wrappedLines = splitTextIntoLines(pdf, trimmedLine || ' ', colWidths[0] - 4, 8);
          wrappedLines.forEach((wrappedLine, wrappedIndex) => {
            pdf.text(wrappedLine || ' ', startX + 2, currentY, { 
              maxWidth: colWidths[0] - 4,
              align: 'left'
            });
            currentY += 4;
          });
        } else {
          // Empty line - add some spacing
          currentY += 4;
        }
      });
      
      yPos += cellHeight;
    });
    
    return yPos;
  };

  // Helper function to render robots.txt table with preserved newlines
  // eslint-disable-next-line no-unused-vars
  const renderRobotsTable = (pdf, headers, rows, startX, startY, colWidths, margin, pageHeight, checkNewPageFn, rowHeight = 7) => {
    let yPos = startY;
    const headerHeight = 8;
    const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);
    
    // Set font for headers
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    
    // Draw header row with unified border
    const drawHeader = (y) => {
      pdf.rect(startX, y, totalWidth, headerHeight);
      pdf.text(headers[0], startX + 2, y + 5, { maxWidth: colWidths[0] - 4 });
    };
    
    drawHeader(yPos);
    yPos += headerHeight;
    
    // Set font for data rows
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    
    // Draw data rows with page break handling
    rows.forEach((row, rowIndex) => {
      const cellText = row[0] || '-';
      
      // Split by newlines to preserve line breaks
      const contentLines = cellText.split('\n');
      
      // Calculate total height needed for all lines
      let totalLines = 0;
      contentLines.forEach(line => {
        const wrappedLines = splitTextIntoLines(pdf, line.trim(), colWidths[0] - 4, 8);
        totalLines += wrappedLines.length || 1; // At least 1 line even if empty
      });
      
      const cellHeight = Math.max(totalLines * 4 + 4, rowHeight);
      
      // Check if we need a new page
      if (yPos + cellHeight > pageHeight - margin) {
        pdf.addPage();
        yPos = margin;
        // Redraw header on new page
        drawHeader(yPos);
        yPos += headerHeight;
      }
      
      // Draw row with unified border
      pdf.rect(startX, yPos, totalWidth, cellHeight);
      
      // Draw content line by line, preserving newlines
      let currentY = yPos + 5;
      contentLines.forEach((line, lineIndex) => {
        const trimmedLine = line.trim();
        if (trimmedLine || lineIndex === 0) { // Include empty lines or first line
          const wrappedLines = splitTextIntoLines(pdf, trimmedLine || ' ', colWidths[0] - 4, 8);
          wrappedLines.forEach((wrappedLine, wrappedIndex) => {
            pdf.text(wrappedLine || ' ', startX + 2, currentY, { 
              maxWidth: colWidths[0] - 4,
              align: 'left'
            });
            currentY += 4;
          });
        } else {
          // Empty line - add spacing
          currentY += 4;
        }
      });
      
      yPos += cellHeight;
    });
    
    return yPos;
  };

  // Helper function to render table with custom column widths
  const renderTableWithCustomWidths = (pdf, headers, rows, startX, startY, colWidths, margin, pageHeight, checkNewPageFn, rowHeight = 7) => {
    let yPos = startY;
    const headerHeight = 8;
    const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);
    
    // Set font for headers
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    
    // Draw header row with unified border
    const drawHeader = (y) => {
      // Draw outer border for header
      pdf.rect(startX, y, totalWidth, headerHeight);
      
      let currentX = startX;
      headers.forEach((header, colIndex) => {
        const colWidth = colWidths[colIndex];
        // Draw vertical line between columns (except for first column)
        if (colIndex > 0) {
          pdf.line(currentX, y, currentX, y + headerHeight);
        }
        pdf.text(header, currentX + 2, y + 5, { maxWidth: colWidth - 4 });
        currentX += colWidth;
      });
    };
    
    drawHeader(yPos);
    yPos += headerHeight;
    
    // Set font for data rows
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    
    // Draw data rows with page break handling
    rows.forEach((row, rowIndex) => {
      // Calculate cell height based on all cells in the row
      // Add extra padding for remarks column (last column) and details column (index 1)
      const remarksPadding = 3; // Extra bottom padding for remarks
      const detailsPadding = 3; // Extra bottom padding for details column
      const cellHeight = Math.max(...row.map((cell, colIndex) => {
        const colWidth = colWidths[colIndex];
        const lines = splitTextIntoLines(pdf, cell || '', colWidth - 4, 8);
        const baseHeight = lines.length * 4 + 2;
        // Add extra padding for remarks column (last column) or details column (index 1)
        if (colIndex === row.length - 1) {
          return baseHeight + remarksPadding; // Last column (Remarks)
        } else if (colIndex === 1) {
          return baseHeight + detailsPadding; // Details column
        }
        return baseHeight;
      }), rowHeight);
      
      // Check if we need a new page
      if (yPos + cellHeight > pageHeight - margin) {
        pdf.addPage();
        yPos = margin;
        // Redraw header on new page
        drawHeader(yPos);
        yPos += headerHeight;
      }
      
      // Draw row with unified border
      pdf.rect(startX, yPos, totalWidth, cellHeight);
      
      // Draw row cells
      let currentX = startX;
      row.forEach((cell, colIndex) => {
        const colWidth = colWidths[colIndex];
        
        // Draw vertical line between columns (except for first column)
        if (colIndex > 0) {
          pdf.line(currentX, yPos, currentX, yPos + cellHeight);
        }
        
        const cellText = cell || '-';
        const cellMaxWidth = colWidth - 4; // Padding on both sides
        const lines = splitTextIntoLines(pdf, cellText, cellMaxWidth, 8);
        
        // Draw each line with proper spacing
        lines.forEach((line, lineIndex) => {
          const lineY = yPos + (lineIndex * 4) + 5;
          pdf.text(line, currentX + 2, lineY, { 
            maxWidth: cellMaxWidth,
            align: 'left'
          });
        });
        
        currentX += colWidth;
      });
      
      yPos += cellHeight; // No gap between rows - continuous table
    });
    
    return yPos;
  };

  // Helper function to render a table in PDF with page break support
  // eslint-disable-next-line no-unused-vars
  const renderTable = (pdf, headers, rows, startX, startY, maxWidth, margin, pageHeight, checkNewPageFn, rowHeight = 7) => {
    let yPos = startY;
    const colCount = headers.length;
    const colWidth = maxWidth / colCount;
    const headerHeight = 8;
    
    // Set font for headers
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    
    // Draw header row
    const drawHeader = (y) => {
      headers.forEach((header, colIndex) => {
        const xPos = startX + (colIndex * colWidth);
        pdf.rect(xPos, y, colWidth, headerHeight);
        pdf.text(header, xPos + 2, y + 5, { maxWidth: colWidth - 4 });
      });
    };
    
    drawHeader(yPos);
    yPos += headerHeight;
    
    // Set font for data rows
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    
    // Draw data rows with page break handling
    rows.forEach((row, rowIndex) => {
      // Calculate cell height
      const cellHeight = Math.max(...row.map(cell => {
        const lines = splitTextIntoLines(pdf, cell || '', colWidth - 4, 8);
        return lines.length * 4 + 2;
      }), rowHeight);
      
      // Check if we need a new page
      if (yPos + cellHeight > pageHeight - margin) {
        pdf.addPage();
        yPos = margin;
        // Redraw header on new page
        drawHeader(yPos);
        yPos += headerHeight;
      }
      
      // Draw row cells
      row.forEach((cell, colIndex) => {
        const xPos = startX + (colIndex * colWidth);
        pdf.rect(xPos, yPos, colWidth, cellHeight);
        
        const cellText = cell || '-';
        const cellMaxWidth = colWidth - 4; // Padding on both sides
        const lines = splitTextIntoLines(pdf, cellText, cellMaxWidth, 8);
        
        // Draw each line with proper spacing
        lines.forEach((line, lineIndex) => {
          const lineY = yPos + (lineIndex * 4) + 5;
          pdf.text(line, xPos + 2, lineY, { 
            maxWidth: cellMaxWidth,
            align: 'left'
          });
        });
      });
      
      yPos += cellHeight + 1; // Add small gap between rows
    });
    
    return yPos;
  };

  const handleDownloadReport = async () => {
    if (!results || !reportRef.current) return;
    
    // Prevent download during countdown
    if (downloadCountdown > 0) return;
    
    // Show full-page loader while generating report
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setFunFact(randomFact);
    setPageLoaderMessage('Generating report...');
    setShowPageLoader(true);

    // Force light theme for the printed report
    const prevThemeAttr = document.documentElement.getAttribute('data-theme') || 'light';
    if (prevThemeAttr !== 'light') {
      document.documentElement.setAttribute('data-theme', 'light');
    }

    // Wait a moment for theme to apply
    await new Promise(resolve => setTimeout(resolve, 100));

    // Load custom report settings from localStorage
    let reportSettings = {
      logoUrl: '/logo.png',
      leftFooterText: 'Generated by Parrot SEO',
      rightFooterText: 'parrotseo.com',
      watermarkEnabled: true
    };
    
    try {
      const savedSettings = localStorage.getItem('reportSettings');
      const normalizedPlan = (userPlan || 'basic').toLowerCase();
      const isBasicPlan = !normalizedPlan || normalizedPlan === 'basic';

      if (!isBasicPlan && savedSettings) {
        // Only apply custom branding for non-basic plans
        reportSettings = { ...reportSettings, ...JSON.parse(savedSettings) };
      } else if (isBasicPlan) {
        // Force defaults for Basic plan users to avoid using any old custom settings
        reportSettings = {
          logoUrl: '/logo.png',
          leftFooterText: 'Generated by Parrot SEO',
          rightFooterText: 'parrotseo.com',
          watermarkEnabled: true
        };
      }
    } catch (error) {
      console.warn('Error loading report settings:', error);
    }

    try {
      // Create new PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Set margins (15mm = 1.5cm)
      const margin = 15;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Helper to add footer to a specific page
      const addFooterToPage = (pageNumber) => {
        pdf.setPage(pageNumber);
        const footerY = pageHeight - margin + 5;
        pdf.setFontSize(7); // Reduced font size
        pdf.setFont('helvetica', 'normal');
        // Left side: custom footer text
        pdf.text(reportSettings.leftFooterText || 'Generated by Parrot SEO', margin, footerY);
        // Right side: custom footer text
        const rightText = reportSettings.rightFooterText || 'parrotseo.com';
        const rightTextWidth = pdf.getTextWidth(rightText);
        pdf.text(rightText, pageWidth - margin - rightTextWidth, footerY);
      };

      // Helper to add footer to current page
      const addFooter = () => {
        const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
        addFooterToPage(currentPage);
      };

      // Helper to add new page if needed
      const checkNewPage = (requiredHeight = 10) => {
        if (yPosition + requiredHeight > pageHeight - margin - 10) { // Reserve space for footer
          addFooter(); // Add footer before new page
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper to check if there's space for title and table, move to next page if not
      const checkSpaceForTitleAndTable = (estimatedTableHeight = 30) => {
        const titleHeight = 6; // Height for title text
        const totalRequired = titleHeight + estimatedTableHeight;
        // Add extra buffer to ensure we have enough space
        const buffer = 10; // Extra buffer space
        if (yPosition + totalRequired + buffer > pageHeight - margin - 10) {
          addFooter(); // Add footer before new page
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper to estimate robots.txt table height based on content
      const estimateRobotsTableHeight = (content) => {
        if (!content) return 30;
        // Estimate: each line takes about 4mm, plus borders and padding
        // Account for text wrapping (assume max width causes some wrapping)
        const lines = content.split('\n').length;
        // More conservative estimate: assume some lines might wrap
        const estimatedLines = Math.ceil(lines * 1.2); // Add 20% for potential wrapping
        const estimatedHeight = Math.max(estimatedLines * 4 + 12, 30); // Minimum 30mm, plus padding
        return Math.min(estimatedHeight, 120); // Cap at 120mm to avoid over-estimation
      };

      // Add header with logo on right and title/URL on left
      const headerStartY = yPosition;
      let logoHeight = 0;
      let logoWidth = 0;
      
      // Try to load and add logo on the right side (use custom logo if available)
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.src = reportSettings.logoUrl || '/logo.png';
        
        await new Promise((resolve) => {
          logoImg.onload = () => {
            try {
              // Add logo on the right side (max height 20mm, maintain aspect ratio)
              logoHeight = 20;
              logoWidth = (logoImg.width / logoImg.height) * logoHeight;
              const logoX = pageWidth - margin - logoWidth;
              pdf.addImage(logoImg, 'PNG', logoX, headerStartY, logoWidth, logoHeight);
              resolve();
            } catch (error) {
              console.warn('Could not add logo to PDF:', error);
              resolve(); // Continue without logo
            }
          };
          logoImg.onerror = () => {
            console.warn('Could not load logo image');
            resolve(); // Continue without logo
          };
          // Timeout after 2 seconds
          setTimeout(() => resolve(), 2000);
        });
      } catch (error) {
        console.warn('Error loading logo:', error);
      }

      // Add report title on the left side
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Website Audit Report', margin, headerStartY + 8, { maxWidth: maxWidth * 0.6 });
      
      // Add URL below title on the left side
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      const urlText = `Analyzed Site: ${results.url}`;
      const urlLines = splitTextIntoLines(pdf, urlText, maxWidth * 0.6, 12);
      let currentY = headerStartY + 16;
      urlLines.forEach(line => {
        pdf.text(line, margin, currentY, { maxWidth: maxWidth * 0.6 });
        currentY += 8;
      });
      
      // Set yPosition to the bottom of header (logo or title/URL, whichever is taller)
      yPosition = Math.max(headerStartY + logoHeight, currentY) + 12; // Add spacing before content

      // Extract and add content from each section
      const sections = reportRef.current.querySelectorAll('.result-section');
      
      sections.forEach(section => {
        checkNewPage(15);
        
        // Get section title
        const sectionTitle = section.querySelector('.section-title');
        let sectionTitleText = '';
        if (sectionTitle) {
          sectionTitleText = sectionTitle.textContent.trim();
          checkNewPage(12);
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          const titleLines = splitTextIntoLines(pdf, sectionTitleText, maxWidth, 14);
          titleLines.forEach(line => {
            checkNewPage(9);
            pdf.text(line, margin, yPosition, { maxWidth: maxWidth });
            yPosition += 9;
          });
          yPosition += 2; // Add spacing after title
        }

        // Get all detail rows in this section and create a table
        const detailRows = section.querySelectorAll('.detail-row');
        
        // Check for robots.txt content - only in "Robots & Crawling Analysis" section
        const isRobotsSection = sectionTitleText === 'Robots & Crawling Analysis';
        const robotsContentElement = section.querySelector('.code-block');
        let robotsContent = '';
        if (robotsContentElement && isRobotsSection) {
          robotsContent = robotsContentElement.textContent.trim();
        }
        
        if (detailRows.length > 0) {
          // Prepare table data
          const tableHeaders = ['Metric', 'Status', 'Value', 'Remarks'];
          const tableRows = [];
          
          detailRows.forEach(row => {
            // Extract metric name
            const metricSection = row.querySelector('.row-metric-section .row-label');
            const remarkSection = row.querySelector('.row-remark-section');
            // eslint-disable-next-line no-unused-vars
            const statusSection = row.querySelector('.row-status-icon');
            
            if (metricSection && remarkSection) {
              // Get metric name
              const metricName = metricSection.textContent.trim();
              
              // Get status
              let status = 'N/A';
              if (row.classList.contains('detail-good')) {
                status = 'Good';
              } else if (row.classList.contains('detail-warning')) {
                status = 'Warning';
              } else if (row.classList.contains('detail-error')) {
                status = 'Error';
              }
              
              // Get value and remark
              const valueElement = remarkSection.querySelector('.explanation-value');
              const remarkElement = remarkSection.querySelector('.explanation-remark');
              
              const value = valueElement ? valueElement.textContent.trim() : '';
              let remark = '';
              
              if (remarkElement) {
                remark = remarkElement.textContent.trim().replace(/^—\s*/, ''); // Remove leading dash
              } else {
                // If no separate remark, use the full text
                const fullText = remarkSection.textContent.trim();
                if (value) {
                  remark = fullText.replace(value, '').trim().replace(/^—\s*/, '');
                } else {
                  remark = fullText;
                }
              }
              
              // Clean up value (remove extra spaces, newlines)
              const cleanValue = value.replace(/\s+/g, ' ').trim() || 'N/A';
              const cleanRemark = remark.replace(/\s+/g, ' ').trim() || 'No remarks';
              
              tableRows.push([metricName, status, cleanValue, cleanRemark]);
            }
          });
          
          // Render table if we have data
          if (tableRows.length > 0) {
            checkNewPage(30);
            // Use custom column widths: Metric (25%), Status (15%), Value (25%), Remarks (35%)
            const colWidths = [maxWidth * 0.25, maxWidth * 0.15, maxWidth * 0.25, maxWidth * 0.35];
            yPosition = renderTableWithCustomWidths(pdf, tableHeaders, tableRows, margin, yPosition, colWidths, margin, pageHeight, checkNewPage);
            yPosition += 10; // Add spacing after table
          }
        }
        
        // Handle robots.txt content in a separate table (only in Robots & Crawling Analysis section)
        if (robotsContent && isRobotsSection) {
          // Estimate table height based on actual content
          const estimatedHeight = estimateRobotsTableHeight(robotsContent);
          checkSpaceForTitleAndTable(estimatedHeight); // Check space for title + table
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Detected robots.txt:', margin, yPosition);
          yPosition += 6;
          
          // Preserve newlines in robots.txt content - keep all lines including empty ones
          // eslint-disable-next-line no-unused-vars
          const formattedRobotsContent = robotsContent;
          
          // Create a single-column table with robots.txt content (no header to avoid duplication)
          // eslint-disable-next-line no-unused-vars
          const robotsHeaders = []; // Empty header array - we already have "Detected robots.txt:" label
          const robotsRows = [[formattedRobotsContent]];
          
          if (robotsRows.length > 0) {
            const robotsColWidths = [maxWidth];
            // Use a custom rendering that preserves newlines
            yPosition = renderRobotsTableWithoutHeader(pdf, robotsRows, margin, yPosition, robotsColWidths, margin, pageHeight, checkNewPage);
            yPosition += 10;
          }
        }
        
        // Handle sitemap URLs table (all URLs in one cell, like robots.txt)
        const sitemapLinks = section.querySelectorAll('.row-link-stack a');
        if (sitemapLinks.length > 0) {
          checkSpaceForTitleAndTable(20); // Check space for title + table
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Detected Sitemaps:', margin, yPosition);
          yPosition += 6;
          
          // Combine all sitemap URLs into one string, separated by newlines
          const allSitemapUrls = Array.from(sitemapLinks)
            .map(link => link.textContent.trim() || link.href)
            .join('\n');
          
          // Create a single-column table with all sitemap URLs
          const sitemapRows = [[allSitemapUrls]];
          
          if (sitemapRows.length > 0) {
            const sitemapColWidths = [maxWidth];
            // Use a custom rendering that preserves newlines (no header)
            yPosition = renderRobotsTableWithoutHeader(pdf, sitemapRows, margin, yPosition, sitemapColWidths, margin, pageHeight, checkNewPage);
            yPosition += 10;
          }
        }
        
        // Handle schema types table (all schemas as tags in one cell)
        const schemaTags = section.querySelectorAll('.tag-container .schema-tag');
        if (schemaTags.length > 0) {
          checkSpaceForTitleAndTable(20); // Check space for title + table
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Detected Schema Types:', margin, yPosition);
          yPosition += 6;
          
          // Combine all schema tags into one string, separated by newlines
          const allSchemaTags = Array.from(schemaTags)
            .map(tag => tag.textContent.trim())
            .join('\n');
          
          // Create a single-column table with all schema tags
          const schemaRows = [[allSchemaTags]];
          
          if (schemaRows.length > 0) {
            const schemaColWidths = [maxWidth];
            // Use a custom rendering that preserves newlines (no header)
            yPosition = renderRobotsTableWithoutHeader(pdf, schemaRows, margin, yPosition, schemaColWidths, margin, pageHeight, checkNewPage);
            yPosition += 10;
          }
        }

        // Handle link tables - find parent container to determine link type
        const linkContainers = section.querySelectorAll('.link-list-container');
        linkContainers.forEach(container => {
          checkNewPage(30);
          
          // Find the parent link-detail-row to get context
          const parentDetailRow = container.closest('.link-detail-row');
          
          // Determine title based on DetailRow label above
          let tableTitle = 'Detected Links';
          
          if (parentDetailRow) {
            // Try to find the DetailRow label above this container
            let currentElement = parentDetailRow.previousElementSibling;
            while (currentElement) {
              const labelElement = currentElement.querySelector('.row-label');
              if (labelElement) {
                const labelText = labelElement.textContent.trim();
                if (labelText.includes('Internal Links')) {
                  tableTitle = 'Detected Internal Links';
                } else if (labelText.includes('External Links')) {
                  tableTitle = 'Detected External Links';
                } else if (labelText.includes('NoFollow Links') || labelText.includes('Nofollow Links')) {
                  tableTitle = 'Detected NoFollow Links';
                } else if (labelText.includes('Broken Links')) {
                  tableTitle = 'Detected Broken Links';
                }
                break;
              }
              currentElement = currentElement.previousElementSibling;
            }
          }
          
          const linkRows = container.querySelectorAll('.link-preview-row');
          if (linkRows.length > 0) {
            // Check space for title and table before adding title
            checkSpaceForTitleAndTable(30); // Check space for title + table
            // Add title before table
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.text(tableTitle + ':', margin, yPosition);
            yPosition += 6;
            
            const headers = ['Link Type', 'Anchor Text', 'URL'];
            const rows = [];
            
            linkRows.forEach(row => {
              const typeCell = row.querySelector('.link-preview-type .link-type-tag')?.textContent.trim() || 
                              row.querySelector('.link-preview-type')?.textContent.trim() || '-';
              const anchorCell = row.querySelector('.link-preview-anchor .link-anchor')?.textContent.trim() || 
                               row.querySelector('.link-preview-anchor .link-image-anchor span')?.textContent.trim() || 
                               (row.querySelector('.link-preview-anchor .link-image-anchor img') ? 'Image Link' : '-');
              const urlCell = row.querySelector('.link-preview-url .link-url')?.textContent.trim() || 
                             row.querySelector('.link-preview-url')?.textContent.trim() || '-';
              
              rows.push([typeCell, anchorCell, urlCell]);
            });
            
            if (rows.length > 0) {
              // Use custom column widths: Link Type (20%), Anchor Text (35%), URL (45%)
              const colWidths = [maxWidth * 0.20, maxWidth * 0.35, maxWidth * 0.45];
              yPosition = renderTableWithCustomWidths(pdf, headers, rows, margin, yPosition, colWidths, margin, pageHeight, checkNewPage);
              yPosition += 10; // Add spacing after table
            }
          }
        });

        // Handle image tables - find parent container to determine issue type
        const imageContainers = section.querySelectorAll('.image-list-container');
        imageContainers.forEach(container => {
          checkNewPage(30);
          
          // Find the parent image-detail-row to get context
          const parentDetailRow = container.closest('.image-detail-row');
          
          // Determine title based on remark text or find the DetailRow label above
          let tableTitle = 'Detected Images with Issues';
          
          if (parentDetailRow) {
            // Check the remark text to determine the issue type
            const firstRemark = container.querySelector('.image-remark-text')?.textContent.trim() || '';
            
            if (firstRemark.includes('alt text') || firstRemark.includes('Add descriptive alt')) {
              tableTitle = 'Detected Images with No Alt Text';
            } else if (firstRemark.includes('Compress') || firstRemark.includes('300KB') || firstRemark.includes('speed up page')) {
              tableTitle = 'Detected Large Images (>300KB)';
            } else if (firstRemark.includes('WebP') || firstRemark.includes('modern format')) {
              tableTitle = 'Detected Images Missing WebP Version';
            } else {
              // Try to find the DetailRow label above this container
              let currentElement = parentDetailRow.previousElementSibling;
              while (currentElement) {
                const labelElement = currentElement.querySelector('.row-label');
                if (labelElement) {
                  const labelText = labelElement.textContent.trim();
                  if (labelText.includes('Alt Text')) {
                    tableTitle = 'Detected Images with No Alt Text';
                  } else if (labelText.includes('Large Images')) {
                    tableTitle = 'Detected Large Images (>300KB)';
                  } else if (labelText.includes('WebP')) {
                    tableTitle = 'Detected Images Missing WebP Version';
                  }
                  break;
                }
                currentElement = currentElement.previousElementSibling;
              }
            }
          }
          
          const imageRows = container.querySelectorAll('.image-preview-row');
          if (imageRows.length > 0) {
            // Check space for title and table before adding title
            checkSpaceForTitleAndTable(30); // Check space for title + table
            // Add title before table
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.text(tableTitle + ':', margin, yPosition);
            yPosition += 6;
            
            const headers = ['Image', 'Details', 'Remarks'];
            const rows = [];
            
            imageRows.forEach(row => {
              const imageCell = 'Image'; // Placeholder since we can't render images as text
              const detailsCell = row.querySelector('.image-preview-info')?.textContent.trim() || '-';
              const remarksCell = row.querySelector('.image-preview-remark .image-remark-text')?.textContent.trim() || 
                                row.querySelector('.image-preview-remark')?.textContent.trim() || '-';
              
              rows.push([imageCell, detailsCell, remarksCell]);
            });
            
            if (rows.length > 0) {
              // Use custom column widths: Image (15%), Details (40%), Remarks (45%)
              const colWidths = [maxWidth * 0.15, maxWidth * 0.40, maxWidth * 0.45];
              yPosition = renderTableWithCustomWidths(pdf, headers, rows, margin, yPosition, colWidths, margin, pageHeight, checkNewPage);
              yPosition += 10; // Add spacing after table
            }
          }
        });

        // Handle headings - group by type and create separate tables
        // Find all heading containers with their labels
        const headingGroups = [];
        const allAdditionalContent = section.querySelectorAll('.additional-content');
        
        allAdditionalContent.forEach(additionalContent => {
          const labelElement = additionalContent.querySelector('.additional-content-label');
          const headingContainer = additionalContent.querySelector('.heading-container');
          
          if (labelElement && headingContainer) {
            const labelText = labelElement.textContent.trim();
            // Extract heading type from label (e.g., "Detected H1 Headings:" -> "H1")
            const headingTypeMatch = labelText.match(/Detected\s+(H[1-6])\s+Headings?:/i);
            if (headingTypeMatch) {
              const headingType = headingTypeMatch[1];
              const headingBoxes = headingContainer.querySelectorAll('.heading-box');
              const headings = Array.from(headingBoxes)
                .map(box => box.textContent.trim())
                .filter(text => text.length > 0);
              
              if (headings.length > 0) {
                headingGroups.push({ type: headingType, headings });
              }
            }
          }
        });
        
        // Create separate table for each heading type
        headingGroups.forEach(group => {
          checkSpaceForTitleAndTable(20); // Check space for title + table
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`Detected ${group.type} Headings:`, margin, yPosition);
          yPosition += 6;
          
          // Create table with columns: Heading Type and Detected Heading
          const tableHeaders = ['Heading Type', 'Detected Heading'];
          const tableRows = group.headings.map(heading => [group.type, heading]);
          
          if (tableRows.length > 0) {
            const colWidths = [maxWidth * 0.2, maxWidth * 0.8];
            yPosition = renderTableWithCustomWidths(pdf, tableHeaders, tableRows, margin, yPosition, colWidths, margin, pageHeight, checkNewPage);
            yPosition += 10; // Add spacing after table
          }
        });

        // Handle other additional content (exclude robots.txt, sitemaps, schemas, and headings which are handled separately)
        const additionalContent = section.querySelectorAll('.additional-content:not(.heading-container):not(.link-list-container):not(.image-list-container)');
        additionalContent.forEach(content => {
          // Skip robots.txt additional content if we're in the Robots section and already handled it
          if (isRobotsSection && robotsContent) {
            const hasRobotsContent = content.querySelector('.code-block');
            if (hasRobotsContent) {
              return; // Skip this content, already handled in separate table
            }
          }
          
          // Skip sitemap URLs if already handled in separate table
          const hasSitemapLinks = content.querySelector('.row-link-stack');
          if (hasSitemapLinks) {
            return; // Skip this content, already handled in separate table
          }
          
          // Skip schema tags if already handled in separate table
          const hasSchemaTags = content.querySelector('.tag-container .schema-tag');
          if (hasSchemaTags) {
            return; // Skip this content, already handled in separate table
          }
          
          // Skip heading containers if already handled in separate tables
          const hasHeadingContainer = content.querySelector('.heading-container');
          if (hasHeadingContainer) {
            return; // Skip this content, already handled in separate tables
          }
          
          checkNewPage(10);
          const contentText = content.textContent.trim();
          if (contentText) {
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            const contentLines = splitTextIntoLines(pdf, contentText, maxWidth - 5, 9);
            contentLines.forEach(line => {
              checkNewPage(5);
              pdf.text(line, margin + 5, yPosition, { maxWidth: maxWidth - 5 });
              yPosition += 5;
            });
            yPosition += 5; // Add spacing after content
          }
        });

        yPosition += 12; // Space between sections
      });

      // Add footer and watermark logo to all pages
      let logoImageData = null;
      
      // Try to load logo for watermark (only if enabled)
      if (reportSettings.watermarkEnabled) {
        try {
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.src = reportSettings.logoUrl || '/logo.png';
          
          logoImageData = await new Promise((resolve) => {
            logoImg.onload = () => {
            try {
              // Create a canvas to make the logo semi-transparent
              const canvas = document.createElement('canvas');
              canvas.width = logoImg.width;
              canvas.height = logoImg.height;
              const ctx = canvas.getContext('2d');
              
              // Set opacity for watermark effect (0.1 = 10% opacity)
              ctx.globalAlpha = 0.1;
              ctx.drawImage(logoImg, 0, 0);
              
              // Convert to data URL
              const imageData = canvas.toDataURL('image/png');
              resolve(imageData);
            } catch (error) {
              console.warn('Could not create watermark logo:', error);
              resolve(null);
            }
          };
          logoImg.onerror = () => {
            console.warn('Could not load logo for watermark');
            resolve(null);
          };
          // Timeout after 2 seconds
          setTimeout(() => resolve(null), 2000);
        });
        } catch (error) {
          console.warn('Error loading logo for watermark:', error);
        }
      }

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        
        // Add footer
        addFooterToPage(i);
        
        // Add watermark logo in center if available and enabled
        if (logoImageData && reportSettings.watermarkEnabled) {
          try {
            // Get original logo dimensions for aspect ratio
            const originalLogoImg = new Image();
            originalLogoImg.src = reportSettings.logoUrl || '/logo.png';
            
            await new Promise((resolve) => {
              originalLogoImg.onload = () => {
                try {
                  // Calculate center position
                  const watermarkWidth = 80; // Width in mm
                  const watermarkHeight = (originalLogoImg.height / originalLogoImg.width) * watermarkWidth;
                  const centerX = (pageWidth - watermarkWidth) / 2;
                  const centerY = (pageHeight - watermarkHeight) / 2;
                  
                  // Add watermark logo in center (already has transparency from canvas)
                  pdf.addImage(logoImageData, 'PNG', centerX, centerY, watermarkWidth, watermarkHeight);
                } catch (error) {
                  console.warn('Could not add watermark logo:', error);
                }
                resolve();
              };
              originalLogoImg.onerror = () => resolve();
              setTimeout(() => resolve(), 500);
            });
          } catch (error) {
            console.warn('Error adding watermark logo:', error);
          }
        }
      }
      
      // Save PDF
      const filename = `SEO-Analysis-${results.url.replace(/https?:\/\//, '').replace(/\//g, '-')}.pdf`;
      pdf.save(filename);
      
      // Start 2-minute countdown (120 seconds) after successful download
      const targetUrl = currentProject?.url || url || results?.url;
      const downloadKey = getDownloadCountdownKey(targetUrl);
      if (downloadKey) {
        const countdownEndTime = Date.now() + (2 * 60 * 1000);
        localStorage.setItem(downloadKey, countdownEndTime.toString());
        setDownloadCountdown(120);
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      // Restore theme
      document.documentElement.setAttribute('data-theme', prevThemeAttr);
      // Hide full-page loader
      setShowPageLoader(false);
    }
  };

  const handleAnalyze = async (e, projectUrl = null) => {
    if (e) {
      e.preventDefault();
    }
    
    const targetUrl = projectUrl || url;
    if (!targetUrl || !targetUrl.trim()) {
      setError('Please enter a URL');
      return;
    }

    // Prevent analyze during countdown
    if (analyzeCountdown > 0) return;

    // Show full-page loader while analyzing
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setFunFact(randomFact);
    setPageLoaderMessage('Analyzing website...');
    setShowPageLoader(true);
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await axios.post(getApiUrl('analyze'), { url: targetUrl }, axiosConfig);
      const auditResults = response.data;
      setResults(auditResults);
      
      // Save results to localStorage cache
      const cacheKey = getCacheKey(targetUrl);
      if (cacheKey) {
        try {
          const cacheData = {
            data: auditResults,
            cachedAt: new Date().toISOString(),
            timestamp: new Date().toISOString()
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (err) {
          console.error('Error saving audit results to cache:', err);
        }
      }
      
      // Save results to project if we have a current project
      if (currentProject) {
        updateProject(currentProject.id, {
          auditResults: auditResults,
          lastAuditDate: new Date().toISOString()
        });
      }

      // Start 2-minute countdown (120 seconds) for analyze action
      const analyzeKey = getAnalyzeCountdownKey(targetUrl);
      if (analyzeKey) {
        const countdownEndTime = Date.now() + (2 * 60 * 1000);
        localStorage.setItem(analyzeKey, countdownEndTime.toString());
        setAnalyzeCountdown(120);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to analyze website. Please try again.');
    } finally {
      setLoading(false);
      setShowPageLoader(false);
    }
  };

  const handleReAudit = () => {
    if (currentProject) {
      handleAnalyze(null, currentProject.url);
    }
  };

  return (
    <div className="seo-analyzer">
      {showPageLoader && (
        <div className="page-loader-overlay">
          <div className="page-loader-content">
            {funFact && (
              <div className="fun-fact-container">
                <div className="fun-fact-icon">💡</div>
                <p className="fun-fact-text">{funFact}</p>
              </div>
            )}
            <div className="page-loader-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p className="page-loader-text">{pageLoaderMessage}</p>
            <p className="page-loader-message">
              This will take a few seconds, depends on the website size. Kindly wait until our algorithms analyze your website.
            </p>
          </div>
        </div>
      )}
      <header className="header">
        <div className="header-container">
          <div className="header-actions">
            <a href="/pricing" className="header-menu-item">
              Plans & Pricing
            </a>
            {user && (
              <div className="user-info">
                <button
                  className="user-icon-button"
                  onClick={() => {
                    window.location.href = '/dashboard/account-settings';
                  }}
                  aria-label="Account settings"
                  title="Account settings"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="container">
        {!currentProject && (
          <form onSubmit={handleAnalyze} className="analyzer-form">
            <div className="input-group">
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter website URL (e.g., example.com or https://example.com)"
                className="url-input"
                disabled={loading}
              />
              <button 
                type="submit" 
                className="analyze-btn" 
                disabled={loading || (currentProject && currentProject.url === url && analyzeCountdown > 0)}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Analyzing...
                  </>
                ) : (currentProject && currentProject.url === url && analyzeCountdown > 0) ? (
                  `Analyze Website (${Math.floor(analyzeCountdown / 60)}:${String(analyzeCountdown % 60).padStart(2, '0')})`
                ) : (
                  'Analyze Website'
                )}
              </button>
            </div>
          </form>
        )}

        {currentProject && (
          <div className="site-audit-header">
            <div className="site-audit-title">
              <span className="site-audit-label">Site Audit</span>
              <span className="site-audit-separator">:</span>
              <span className="site-audit-url">{currentProject.url}</span>
            </div>
            <div className="site-audit-right">
              {(results || currentProject?.lastAuditDate) && (
                <div className="last-crawl-info">
                  <div className="last-crawl-label">Last Crawl:</div>
                  <div className="last-crawl-date">
                    {results 
                      ? new Date(results.timestamp).toLocaleString()
                      : currentProject?.lastAuditDate 
                        ? new Date(currentProject.lastAuditDate).toLocaleString()
                        : 'Never'}
                  </div>
                </div>
              )}
              <div className="site-audit-actions">
                <button
                  type="button"
                  className="recrawl-btn"
                  onClick={handleReAudit}
                  disabled={loading || analyzeCountdown > 0}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Analyzing...
                    </>
                  ) : analyzeCountdown > 0 ? (
                    `RE-ANALYZE (${Math.floor(analyzeCountdown / 60)}:${String(analyzeCountdown % 60).padStart(2, '0')})`
                  ) : (
                    'RE-ANALYZE'
                  )}
                </button>
                <button 
                  className="download-report-btn" 
                  onClick={handleDownloadReport}
                  disabled={downloadCountdown > 0 || !results}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  <span>
                    {downloadCountdown > 0 
                      ? `Download Report (${Math.floor(downloadCountdown / 60)}:${String(downloadCountdown % 60).padStart(2, '0')})`
                      : 'Download Report'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {results && (
          <div ref={reportRef}>
            {!currentProject && (
              <div className="results-header">
                <h2 className="results-title">Analysis Results</h2>
                <div className="results-header-info">
                  <div className="url-display">
                    <strong>Analyzed Site:</strong> {results.url}
                  </div>
                  <div className="timestamp">
                    Analyzed: {new Date(results.timestamp).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  className="download-report-btn"
                  onClick={handleDownloadReport}
                  disabled={downloadCountdown > 0}
                >
                  {downloadCountdown > 0 
                    ? `Download PDF (${Math.floor(downloadCountdown / 60)}:${String(downloadCountdown % 60).padStart(2, '0')})`
                    : 'Download PDF'}
                </button>
              </div>
            )}
            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Security & Redirection Analysis</h3>
                <CircularProgress percentage={calculateSecurityScore(results.security)} />
              </div>
              <div className="section-content">
                <DetailRow
                  label="SSL Certificate"
                  value={results.security.sslCertificate ? 'Valid certificate detected' : 'Not detected'}
                  remark={results.security.sslCertificate
                    ? 'Your website uses HTTPS (the secure version of HTTP), which encrypts data between visitors and your site. This protects sensitive information and makes visitors feel safe. Make sure to renew your certificate before it expires to avoid browser security warnings.'
                    : 'Your website doesn\'t have an SSL certificate, which means it\'s not using HTTPS. This is important for security and trust—visitors expect to see the padlock icon in their browser. Install an SSL certificate (many hosting providers offer free ones) to encrypt your website traffic.'}
                  status={results.security.sslCertificate ? 'good' : 'warning'}
                />
                <DetailRow
                  label="HTTPS Handling"
                  value={results.security.httpsRedirection ? 'HTTPS enforced' : 'HTTP allowed'}
                  remark={results.security.httpsRedirection
                    ? 'Great! Your website automatically redirects all visitors from the insecure HTTP version to the secure HTTPS version. This ensures everyone uses the encrypted connection, which is better for security and SEO.'
                    : 'Your website allows both HTTP and HTTPS access. You should set up automatic redirects so all visitors are sent to the HTTPS version. This prevents duplicate content issues and ensures everyone uses the secure connection.'}
                  status={results.security.httpsRedirection ? 'good' : 'warning'}
                />
                <DetailRow
                  label="WWW Redirection"
                  value={results.security.wwwRedirection?.type || 'No redirection set'}
                  remark={results.security.wwwRedirection?.redirected
                    ? 'Your website properly redirects between www and non-www versions (like example.com and www.example.com). This is good—just make sure all your internal links use the same version consistently.'
                    : 'This is a critical SEO issue! Your website doesn\'t redirect between www and non-www versions. Search engines may see your site as two separate websites, which can cause duplicate content problems and split your SEO rankings. You must choose one version (either with www or without www) and set up a permanent redirect from the other version immediately.'}
                  status={results.security.wwwRedirection?.redirected ? 'good' : 'error'}
                />
              </div>
            </section>

            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Robots & Crawling Analysis</h3>
                <CircularProgress percentage={calculateRobotsScore(results.robots)} />
              </div>
              <div className="section-content">
                {(() => {
                  const { remark, status } = getRobotsRemark(results.robots);
                  return (
                    <>
                      <DetailRow
                        label="robots.txt"
                        value={results.robots.exists ? 'Found' : 'Not found'}
                        remark={remark}
                        status={status}
                        additionalContent={
                          results.robots.exists && results.robots.content ? (
                            <div>
                              <div className="additional-content-label">robots.txt Content:</div>
                              <pre className="code-block">{results.robots.content.substring(0, 500)}...</pre>
                            </div>
                          ) : null
                        }
                      />
                      {results.robots.exists && (
                        <DetailRow
                          label="Accessibility"
                          value={results.robots.accessible ? 'Reachable (HTTP 200)' : 'Blocked or 4xx'}
                          remark={results.robots.accessible
                            ? 'Search engines can successfully read your robots.txt file. This is good—just make sure the rules inside are correct and up to date with your current website structure.'
                            : 'Search engines can\'t access your robots.txt file, which means they can\'t read your crawling instructions. Check your server settings to make sure the file is publicly accessible and returns a 200 status code.'}
                          status={results.robots.accessible ? 'good' : 'warning'}
                        />
                      )}
                    </>
                  );
                })()}
              </div>
            </section>

            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Structured Data & Sitemaps Analysis</h3>
                <CircularProgress percentage={calculateSchemaSitemapScore(results.schema, results.sitemaps)} />
              </div>
              <div className="section-content">
                {(() => {
                  const schemaSummary = getSchemaRemark(results.schema);
                  const sitemapSummary = getSitemapRemark(results.sitemaps);
                  return (
                    <>
                      <DetailRow
                        label="Schema Markup"
                        value={results.schema.found ? 'Detected' : 'Not detected'}
                        remark={schemaSummary.remark}
                        status={schemaSummary.status}
                        additionalContent={
                          results.schema.found && results.schema.types.length > 0 ? (
                            <div>
                              <div className="additional-content-label">Detected Schema Types:</div>
                              {formatTags(results.schema.types)}
                            </div>
                          ) : null
                        }
                      />
                      <DetailRow
                        label="Sitemaps"
                        value={results.sitemaps.found ? 'Declared' : 'Not declared'}
                        remark={sitemapSummary.remark}
                        status={sitemapSummary.status}
                        additionalContent={
                          results.sitemaps.sitemapUrls.length > 0 ? (
                            <div>
                              <div className="additional-content-label">Sitemap URLs:</div>
                              <div className="row-link-stack">
                                {results.sitemaps.sitemapUrls.map((sitemapUrl, idx) => (
                                  <a key={idx} href={sitemapUrl} target="_blank" rel="noopener noreferrer">
                                    {sitemapUrl}
                                  </a>
                                ))}
                              </div>
                            </div>
                          ) : null
                        }
                      />
                    </>
                  );
                })()}
              </div>
            </section>

            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Meta Information Analysis</h3>
                <CircularProgress percentage={calculateMetaScore(results.meta)} />
              </div>
              <div className="section-content">
                {(() => {
                  const titleFeedback = getLengthFeedback(results.meta.title, MIN_TITLE, MAX_TITLE, 'Meta title');
                  const descFeedback = getLengthFeedback(results.meta.description, MIN_DESCRIPTION, MAX_DESCRIPTION, 'Meta description');
                  return (
                    <>
                      <DetailRow
                        label="Meta Title"
                        value={results.meta.title}
                        remark={titleFeedback.remark}
                        status={titleFeedback.status}
                      />
                      <DetailRow
                        label="Meta Description"
                        value={results.meta.description}
                        remark={descFeedback.remark}
                        status={descFeedback.status}
                      />
                    </>
                  );
                })()}
                <DetailRow
                  label="Meta Robots"
                  value={results.meta.robots || 'Not specified'}
                  remark={
                    results.meta.robots?.toLowerCase().includes('noindex')
                      ? 'Your page is set to "noindex", which tells search engines not to show this page in search results. Only use this if you intentionally want to hide the page from search. If you want this page to appear in search results, remove the noindex directive.'
                      : 'Your page allows search engines to index it, which means it can appear in search results. This is usually what you want unless you have a specific reason to hide the page.'
                  }
                  status={results.meta.robots?.toLowerCase().includes('noindex') ? 'error' : 'good'}
                />
                <DetailRow
                  label="Indexing Status"
                  value={results.meta.indexingStatus}
                  remark={
                    results.meta.indexingStatus === 'index'
                      ? 'Your page is set to be indexed by search engines, which means it can appear in search results. Monitor your page\'s performance in Google Search Console to see how it\'s performing.'
                      : 'Your page is set to "noindex", which prevents it from appearing in search results. If you want people to find this page through search, you need to remove the noindex setting.'
                  }
                  status={results.meta.indexingStatus === 'index' ? 'good' : 'warning'}
                />
                <DetailRow
                  label="Open Graph Image"
                  value={
                    results.meta.ogImage
                      ? (
                        <div className="og-image-inline">
                          <img src={results.meta.ogImage} alt="OG preview" onError={(e) => (e.target.style.display = 'none')} />
                          <a href={results.meta.ogImage} target="_blank" rel="noopener noreferrer">
                            {results.meta.ogImage}
                          </a>
                          {results.meta.ogImageDimensions && (
                            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                              Dimensions: {results.meta.ogImageDimensions.width} × {results.meta.ogImageDimensions.height} pixels
                            </div>
                          )}
                        </div>
                      )
                      : null
                  }
                  remark={
                    results.meta.ogImage
                      ? (() => {
                          const isOptimalSize = results.meta.ogImageDimensions && 
                            results.meta.ogImageDimensions.width === 1200 && 
                            results.meta.ogImageDimensions.height === 630;
                          if (isOptimalSize) {
                            return 'You have an Open Graph image set up with the optimal dimensions (1200×630 pixels), which is the image that appears when someone shares your page on social media. This size ensures your image looks great on all social platforms.';
                          } else if (results.meta.ogImageDimensions) {
                            return `You have an Open Graph image set up, which is the image that appears when someone shares your page on social media. However, your image is ${results.meta.ogImageDimensions.width}×${results.meta.ogImageDimensions.height} pixels. For the best appearance on social media, use an image that's exactly 1200×630 pixels.`;
                          } else {
                            return 'You have an Open Graph image set up, which is the image that appears when someone shares your page on social media. For the best appearance, use an image that\'s 1200×630 pixels.';
                          }
                        })()
                      : 'You don\'t have an Open Graph image set up. When someone shares your page on social media, they\'ll see a generic preview. Add an og:image tag to control what image appears in social media shares.'
                  }
                  status={
                    results.meta.ogImage
                      ? (() => {
                          const isOptimalSize = results.meta.ogImageDimensions && 
                            results.meta.ogImageDimensions.width === 1200 && 
                            results.meta.ogImageDimensions.height === 630;
                          return isOptimalSize ? 'good' : 'warning';
                        })()
                      : 'warning'
                  }
                />
              </div>
            </section>

            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Technical Analysis</h3>
                <CircularProgress percentage={calculateTechnicalScore(results.meta)} />
              </div>
              <div className="section-content">
                <DetailRow
                  label="Viewport"
                  value={results.meta.viewport || 'Not set'}
                  remark={
                    results.meta.viewport
                      ? 'Your page has a viewport setting, which helps it display properly on mobile devices. This is essential for mobile-friendly websites. Keep testing your site on real phones and tablets to ensure it looks good.'
                      : 'Your page doesn\'t have a viewport meta tag, which means it might not display correctly on mobile devices. Add a viewport tag so your website adapts properly to different screen sizes.'
                  }
                  status={results.meta.viewport ? 'good' : 'warning'}
                />
                <DetailRow
                  label="Charset"
                  value={results.meta.charset || 'Not specified'}
                  remark={
                    results.meta.charset
                      ? 'Your page specifies its character encoding (UTF-8), which ensures special characters and symbols display correctly. This is important for international content and prevents text from appearing as garbled characters.'
                      : 'Your page doesn\'t specify a character encoding. While modern browsers usually handle this automatically, it\'s best practice to declare UTF-8 in your page\'s head section to ensure all characters display correctly.'
                  }
                  status={results.meta.charset ? 'good' : 'error'}
                />
                <DetailRow
                  label="Language"
                  value={results.meta.language || 'Not specified'}
                  remark={
                    results.meta.language
                      ? 'Your page declares its language, which helps search engines understand who your content is for and can improve how your page appears in search results for that language.'
                      : 'Your page doesn\'t specify its language. Adding a language attribute helps search engines understand your target audience and can improve your page\'s visibility in search results for that language.'
                  }
                  status={results.meta.language ? 'good' : 'error'}
                />
              </div>
            </section>

            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Heading Structure Analysis</h3>
                <CircularProgress percentage={calculateHeadingScore(results.headings)} />
              </div>
              <div className="section-content">
                <DetailRow
                  label="H1 Heading"
                  value={results.headings.h1Count === 0 ? 'Not found' : `${results.headings.h1Count} detected`}
                  remark={
                    results.headings.h1Count === 0
                      ? 'Your page is missing the main heading (H1 tag). This is the most important heading that tells both visitors and search engines what your page is about. Add one clear, descriptive H1 at the top of your main content.'
                      : results.headings.h1Count > 1
                      ? `You have ${results.headings.h1Count} main headings (H1 tags) on this page. It's best to have only one H1 per page to clearly show what the page is about. Remove the extra ones and use H2 tags for other important sections instead.`
                      : 'Your page has one main heading (H1 tag), which is perfect. This tells both visitors and search engines what your page is about. Make sure it clearly describes your page content.'
                  }
                  status={results.headings.h1Count === 1 ? 'good' : results.headings.h1Count === 0 ? 'error' : 'warning'}
                  additionalContent={
                    results.headings.h1.length > 0 ? (
                      <div>
                        <div className="additional-content-label">Detected H1 Headings:</div>
                        {formatHeadings(results.headings.h1)}
                      </div>
                    ) : null
                  }
                />
                <DetailRow
                  label="H2 Headings"
                  value={results.headings.h2.length === 0 ? 'Not found' : `${results.headings.h2.length} detected`}
                  remark={
                    results.headings.h2.length === 0
                      ? 'Your page doesn\'t have any section headings (H2 tags). These help organize your content into clear sections, making it easier for both readers and search engines to understand your page structure. Add H2 tags to break up your content.'
                      : `Your page has ${results.headings.h2.length} section headings (H2 tags), which helps organize your content into clear sections. This makes it easier for both readers and search engines to understand your page structure.`
                  }
                  status={results.headings.h2.length > 0 ? 'good' : 'warning'}
                  additionalContent={
                    results.headings.h2.length > 0 ? (
                      <div>
                        <div className="additional-content-label">Detected H2 Headings:</div>
                        {formatHeadings(results.headings.h2)}
                      </div>
                    ) : null
                  }
                />
                {results.headings.h3.length > 0 && (
                  <DetailRow
                    label="H3 Headings"
                    value={`${results.headings.h3.length} detected`}
                    remark={`Your page has ${results.headings.h3.length} subsection headings (H3 tags), which further organize your content under H2 sections. This creates a clear hierarchy that helps both readers and search engines understand your content structure.`}
                    status="good"
                    additionalContent={
                      <div>
                        <div className="additional-content-label">Detected H3 Headings:</div>
                        {formatHeadings(results.headings.h3)}
                      </div>
                    }
                  />
                )}
                {results.headings.h4.length > 0 && (
                  <DetailRow
                    label="H4 Headings"
                    value={`${results.headings.h4.length} detected`}
                    remark={`Your page has ${results.headings.h4.length} sub-subsection headings (H4 tags), which further organize your content. This creates a detailed hierarchy that helps structure your content clearly.`}
                    status="good"
                    additionalContent={
                      <div>
                        <div className="additional-content-label">Detected H4 Headings:</div>
                        {formatHeadings(results.headings.h4)}
                      </div>
                    }
                  />
                )}
                {results.headings.h5.length > 0 && (
                  <DetailRow
                    label="H5 Headings"
                    value={`${results.headings.h5.length} detected`}
                    remark={`Your page has ${results.headings.h5.length} H5 headings, which provide additional content organization. While less common, these can be useful for very detailed content structures.`}
                    status="good"
                    additionalContent={
                      <div>
                        <div className="additional-content-label">Detected H5 Headings:</div>
                        {formatHeadings(results.headings.h5)}
                      </div>
                    }
                  />
                )}
                {results.headings.h6.length > 0 && (
                  <DetailRow
                    label="H6 Headings"
                    value={`${results.headings.h6.length} detected`}
                    remark={`Your page has ${results.headings.h6.length} H6 headings, which provide the most granular level of content organization. These are rarely used but can help structure very detailed content.`}
                    status="good"
                    additionalContent={
                      <div>
                        <div className="additional-content-label">Detected H6 Headings:</div>
                        {formatHeadings(results.headings.h6)}
                      </div>
                    }
                  />
                )}
              </div>
            </section>

            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Keyword Visibility Analysis</h3>
                <CircularProgress percentage={calculateKeywordScore(results.keywords)} />
              </div>
              <div className="section-content">
                <DetailRow
                  label="Meta Keywords"
                  value={results.keywords.metaKeywords || 'Not provided'}
                  remark="Meta keywords are largely ignored by search engines. Focus on on-page usage instead."
                  status="good"
                />
                <DetailRow
                  label="Top Keywords"
                  value={
                    results.keywords.topKeywords.length
                      ? (
                        <div className="keyword-stack">
                          {results.keywords.topKeywords.slice(0, 10).map((kw, idx) => (
                            <span key={idx} className="keyword-chip">
                              {kw.word} <span>{kw.count}×</span>
                            </span>
                          ))}
                        </div>
                      )
                      : null
                  }
                  remark={getKeywordRemark(results.keywords)}
                  status={results.keywords.topKeywords.length ? 'good' : 'warning'}
                />
              </div>
            </section>

            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Content Quality Analysis</h3>
                <CircularProgress percentage={calculateContentScore(results.content)} />
              </div>
              <div className="section-content">
                {(() => {
                  const { remark, status } = getContentRemark(results.content);
                  return (
                    <>
                      <DetailRow
                        label="Word Count"
                        value={`${results.content.wordCount.toLocaleString()} words`}
                        remark={remark}
                        status={status}
                      />
                      <DetailRow
                        label="Paragraphs"
                        value={`${results.content.paragraphCount} paragraphs`}
                        remark="Break content into short paragraphs to keep readers engaged."
                        status="good"
                      />
                      <DetailRow
                        label="Readability"
                        value={(
                          <div className="readability-metrics">
                            <span>{results.content.readability.sentences} sentences</span>
                            <span>{results.content.readability.avgWordsPerSentence} words/sentence</span>
                            <span>{results.content.readability.avgCharsPerWord} chars/word</span>
                          </div>
                        )}
                        remark="Aim for 15–20 words per sentence and 4–6 characters per word for scannability."
                        status="good"
                      />
                    </>
                  );
                })()}
              </div>
            </section>

            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Image Hygiene Analysis</h3>
                <CircularProgress percentage={calculateImageScore(results.images)} />
              </div>
              <div className="section-content">
                {(() => {
                  // eslint-disable-next-line no-unused-vars
                  const { remark, status } = getImageRemark(results.images);
                  return (
                    <>
                      <DetailRow
                        label="Total Images"
                        value={`${results.images.total} image${results.images.total === 1 ? '' : 's'}`}
                        remark="The total number of images found on your page. Compress assets and lazy load lower-fold visuals to improve page speed."
                        status="good"
                      />
                      <DetailRow
                        label="Images Missing Alt Text"
                        value={results.images.withoutAlt === 0 ? 'All images have alt text' : `${results.images.withoutAlt} image${results.images.withoutAlt === 1 ? '' : 's'} missing alt text`}
                        remark={
                          results.images.withoutAlt === 0
                            ? 'All your images have alt text, which is great! Alt text helps screen readers describe images to visually impaired users and provides context to search engines about what your images show.'
                            : `You have ${results.images.withoutAlt} image${results.images.withoutAlt === 1 ? '' : 's'} without alt text. Alt text is important for accessibility and SEO—it helps screen readers describe images to visually impaired users and tells search engines what your images contain. Add descriptive alt text to all images.`
                        }
                        status={results.images.withoutAlt === 0 ? 'good' : 'warning'}
                      />
                      {results.images.missingAltList.length > 0 && (
                        <div className="image-detail-row image-detail-warning">
                          <div className="image-detail-content">
                            {formatImageList(results.images.missingAltList, {
                              remarkText: 'Add descriptive alt text that clearly explains what this image shows.'
                            })}
                          </div>
                        </div>
                      )}
                      <DetailRow
                        label="Large Images"
                        value={results.images.largeImages === 0 ? 'No large images found' : `${results.images.largeImages} image${results.images.largeImages === 1 ? '' : 's'} larger than 300KB`}
                        remark={
                          results.images.largeImages === 0
                            ? 'All your images are optimized and under 300KB, which is great for page speed. Large images can slow down your website, especially on mobile devices.'
                            : `You have ${results.images.largeImages} image${results.images.largeImages === 1 ? '' : 's'} larger than 300KB. Large images can significantly slow down your website, especially on mobile devices. Compress these images or convert them to more efficient formats like WebP to improve page load times.`
                        }
                        status={results.images.largeImages === 0 ? 'good' : 'warning'}
                      />
                      {results.images.largeImageList.length > 0 && (
                        <div className="image-detail-row image-detail-warning">
                          <div className="image-detail-content">
                            {formatImageList(results.images.largeImageList, {
                              showSize: true,
                              remarkText: 'Compress this image below 300KB or convert to WebP/AVIF to speed up page loads.'
                            })}
                          </div>
                        </div>
                      )}
                      <DetailRow
                        label="Images Missing WebP Version"
                        value={results.images.missingWebpList.length === 0 ? 'All images have WebP versions' : `${results.images.missingWebpList.length} image${results.images.missingWebpList.length === 1 ? '' : 's'} missing WebP version`}
                        remark={
                          results.images.missingWebpList.length === 0
                            ? 'All your images have WebP versions available, which is excellent! WebP is a modern image format that provides better compression than JPEG or PNG, resulting in faster page loads.'
                            : `You have ${results.images.missingWebpList.length} image${results.images.missingWebpList.length === 1 ? '' : 's'} without WebP versions. WebP is a modern image format that provides better compression than JPEG or PNG, typically reducing file sizes by 25-35% while maintaining quality. Convert your images to WebP format to improve page load times.`
                        }
                        status={results.images.missingWebpList.length === 0 ? 'good' : 'warning'}
                      />
                      {results.images.missingWebpList.length > 0 && (
                        <div className="image-detail-row image-detail-warning">
                          <div className="image-detail-content">
                            {formatImageList(results.images.missingWebpList, {
                              remarkText: 'Generate a WebP version of this image so browsers can load a lighter, modern format.'
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </section>

            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Link Profile Analysis</h3>
                <CircularProgress percentage={calculateLinkScore(results.links)} />
              </div>
              <div className="section-content">
                {(() => {
                  const { remark, status } = getLinkRemark(results.links);
                  return (
                    <>
                      <DetailRow
                        label="Canonical URL"
                        value={
                          results.links.canonicalUrl ? (
                            <a
                              href={results.links.canonicalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-link"
                            >
                              {results.links.canonicalUrl}
                            </a>
                          ) : 'Not set'
                        }
                        remark={
                          results.links.canonicalUrl
                            ? 'Great — your canonical tag clearly tells search engines which URL is the master copy of this page. Make sure it always points to the preferred clean URL.'
                            : 'You haven\'t set a canonical tag. Add <link rel="canonical"> so search engines don\'t treat URL variations as duplicate content.'
                        }
                        status={results.links.canonicalUrl ? 'good' : 'warning'}
                      />
                      <DetailRow
                        label="Total Links"
                        value={`${results.links.total} links (${results.links.internal} internal / ${results.links.external} external)`}
                        remark={remark}
                        status={status}
                      />
                      <DetailRow
                        label="Internal Links"
                        value={`${results.links.internal} internal link${results.links.internal === 1 ? '' : 's'}`}
                        remark={
                          results.links.internal > 0
                            ? 'Internal links help visitors and crawlers discover more of your content. Keep adding contextual links that guide people deeper into the site.'
                            : 'No internal links were detected on this page. Link to related pages to strengthen site structure and pass authority between key URLs.'
                        }
                        status={results.links.internal > 0 ? 'good' : 'warning'}
                      />
                      {results.links.internalList && results.links.internalList.length > 0 && (
                        <div className="link-detail-row">
                          <div className="link-detail-content">
                            {formatLinkList(results.links.internalList)}
                          </div>
                        </div>
                      )}
                      <DetailRow
                        label="External Links"
                        value={`${results.links.external} external link${results.links.external === 1 ? '' : 's'}`}
                        remark={
                          results.links.external > 0
                            ? 'External links to reputable sources boost credibility. Make sure they open in a new tab and use rel="nofollow" where appropriate.'
                            : 'You don\'t link out to other trusted resources on this page. Consider citing helpful references or partners when it adds value.'
                        }
                        status={results.links.external > 0 ? 'good' : 'warning'}
                      />
                      {results.links.externalList && results.links.externalList.length > 0 && (
                        <div className="link-detail-row">
                          <div className="link-detail-content">
                            {formatLinkList(results.links.externalList)}
                          </div>
                        </div>
                      )}
                      <DetailRow
                        label="Broken Links"
                        value={
                          results.links.broken.length
                            ? `${results.links.broken.length} broken link${results.links.broken.length === 1 ? '' : 's'}`
                            : 'No broken links detected'
                        }
                        remark={
                          results.links.broken.length
                            ? 'Some links return 4xx/5xx errors or time out. Fix or remove them so users and crawlers don’t hit dead ends.'
                            : 'All checked links responded successfully. Keep monitoring regularly to catch future issues.'
                        }
                        status={results.links.broken.length ? 'error' : 'good'}
                      />
                      {results.links.broken.length > 0 && (
                        <div className="link-detail-row link-detail-warning">
                          <div className="link-detail-content">
                            {formatLinkList(
                              results.links.broken.map((link) => ({
                                ...link,
                                tagLabelOverride: 'Broken',
                                tagClassOverride: 'tag-broken'
                              }))
                            )}
                          </div>
                        </div>
                      )}
                      <DetailRow
                        label="NoFollow Links"
                        value={`${results.links.nofollow} tagged as nofollow`}
                        remark={'Use rel="nofollow" on untrusted or paid links to stay compliant.'}
                        status="good"
                      />
                    </>
                  );
                })()}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default SEOAnalyzer;
